<template>
  <Title :pages="[{ icon: 'cube', page: page_title.toUpperCase() }]"></Title>
  <v-container fluid>
    <v-row>
      <v-col cols="12" md="12" v-if="step === 'scan-location'">
        <ScanLocation ref="scanLocationComponent" @location-scanned="handleLocationScanned" />
      </v-col>

      <v-col cols="12" lg="8" v-if="step != 'scan-location'">
        <ScanLPN v-if="step === 'scan'" @scanned="onLPNScanned" ref="scanLPNComponent" />
        <SelectOrder v-if="step === 'select-order'" :current-container="currentContainer" :current-lpn="currentLPN" @order-selected="handleSelectOrder" @back-step="handleBackStep" />
        <ValidateItems
          v-if="step === 'validate'"
          ref="validateItemsComponent"
          :selected-order="selectedOrder"
          :current-lpn="currentLPN"
          :packages="packages"
          :current-package-id="currentPackageId"
          @item-validated="handleValidateItem"
          @validation-finished="handleFinishValidation"
          @add-new-package="handleAddNewPackage"
          @change-package="handleChangePackage"
          @remove-item-from-package="handleRemoveItemFromPackage"
          @back-step="handleBackStep"
        />
        <PackageOrder
          v-if="step === 'package'"
          ref="packageOrderComponent"
          :selected-order="selectedOrder"
          :packages="packages"
          @labels-printed="handlePrintLabels"
          :loading_print_labels="loading_print_labels"
          @back-step="handleBackStep"
        />
        <OrderComplete
          v-if="step === 'complete'"
          ref="orderCompleteComponent"
          :selected-order="selectedOrder"
          :packages="packages"
          :pending-count="getPendingOrdersCount()"
          @next-order="handleNextOrder"
          @new-lpn="handleNewLPN"
          @back-step="handleBackStep"
        />
      </v-col>

      <v-col cols="12" lg="4" v-if="step != 'scan-location'">
        <div>
          <CurrentLocationCard v-if="step !== 'scan-location'" :current-location="currentLocation" @change-location="openLocationModal" />
          <ProcessSteps :current-step="step" />
          <CurrentPackages v-if="step === 'validate' && packages.length > 0" :packages="packages" :current-package-id="currentPackageId" @remove-item-from-package="handleRemoveItemFromPackage" />
          <NextOrder v-if="currentContainer && getNextPendingOrder()" :next-order="getNextPendingOrder()" />
          <LastCompleted v-if="lastCompletedOrder" :last-completed="lastCompletedOrder" @reprint="printLabelZPL" :loading="loading_print_zpl" />
          <CurrentLPN v-if="currentLPN" :current-lpn="currentLPN" :current-container="currentContainer" :pending-orders-count="getPendingOrdersCount()" />
        </div>
      </v-col>
    </v-row>

    <ModalProductAttributes :dialog="showAttributesModal" :product="currentScannedProduct" :lpn-data="currentContainer" @close="closeAttributesModal" @submit="handleAttributesSubmitted" />

    <ModalError :dialog="showError" :message="errorMessage" @close="closeError" />

    <DialogYesNo
      :open="showBackStepDialog"
      title="Confirmar"
      message="¿Está seguro que quiere volver al paso anterior?"
      @cancel="closeBackStepDialog"
      @accept="confirmBackStep"
    />

    <ModalScanLocation ref="modalScanLocation" :dialog="showLocationModal" :required="!currentLocation" @close="closeLocationModal" @location-scanned="handleLocationScanned" />
  </v-container>
</template>

<script>
import ScanLocation from "@/components/Packages/ScanLocation.vue";
import ScanLPN from "./ScanLPN.vue";
import SelectOrder from "./SelectOrder.vue";
import ValidateItems from "./ValidateItems.vue";
import PackageOrder from "./PackageOrder.vue";
import OrderComplete from "./OrderComplete.vue";
import ProcessSteps from "./ProcessSteps.vue";
import NextOrder from "./NextOrder.vue";
import LastCompleted from "./LastCompleted.vue";
import CurrentLPN from "./CurrentLPN.vue";
import CurrentPackages from "@/components/Packages/CurrentPackages.vue";
import ModalProductAttributes from "@/components/Packages/ModalProductAttributes.vue";
import ModalError from "@/components/Packages/ModalError.vue";
import ModalScanLocation from "@/components/Packages/ModalScanLocation.vue";
import CurrentLocationCard from "@/components/Packages/CurrentLocationCard.vue";
import Title from "@/components/Title.vue";
import { packagesService } from "@/libs/ws/packagesService";
import { productoService } from "@/libs/ws/productoService";
import AliasMixin from "@/mixins/AliasMixin.vue";
import GenericMixin from "@/mixins/GenericMixin.vue";
import { ubicacionService } from "@/libs/ws/ubicacionService";
import { shipmentOrderService } from "@/libs/ws/shipmentOrderService";
import BrowserPrintMixin from "@/mixins/BrowserPrint.vue";

export default {
  name: "PackingScreen",
  mixins: [AliasMixin, GenericMixin, BrowserPrintMixin],
  components: {
    ScanLocation,
    ScanLPN,
    SelectOrder,
    ValidateItems,
    PackageOrder,
    OrderComplete,
    ProcessSteps,
    NextOrder,
    LastCompleted,
    CurrentLPN,
    CurrentPackages,
    ModalProductAttributes,
    ModalError,
    ModalScanLocation,
    CurrentLocationCard,
  },
  data() {
    return {
      page_title: "Empaquetado",
      step: "scan-location",
      scannedLPN: "",
      currentLPN: "",
      currentContainer: null,
      originalAssignedLocations: [], // Guardar copia original
      selectedOrder: null,
      originalSelectedOrder: null,
      packageCount: 1,
      lastCompletedOrder: null,
      packages: [{ id: 1, items: [] }],
      currentPackageId: 1,
      itemsFromAssignedLocations: false,
      showAttributesModal: false,
      currentScannedProduct: null,
      currentOrderItem: null,
      showError: false,
      errorMessage: "",
      showBackStepDialog: false,
      currentLocation: null,
      showLocationModal: false,
      loading_print_labels: false,
      productoService: productoService,
      loading_print_zpl: false,
    };
  },
  computed: {
    // Con skip_atributes_item_validation el producto se sigue escaneando, pero los
    // atributos (lote, vencimiento, calidad, etc.) se toman automáticamente de la
    // assigned_location del picking en vez de pedirlos por el modal. Sólo aplica
    // si los items vienen de assigned_locations (si no, no hay de dónde resolverlos).
    skipAttributes() {
      return this.selectedOrder?.client?.skip_atributes_item_validation === true && this.itemsFromAssignedLocations;
    },
  },
  mounted() {
    this.checkCurrentLocation();
    this.loadLastCompletedOrder();
  },
  methods: {
    cloneOrder(order) {
      return order ? JSON.parse(JSON.stringify(order)) : null;
    },

    checkCurrentLocation() {
      // Verificar si hay una ubicación guardada en localStorage
      const savedLocation = localStorage.getItem("current_packing_location");
      if (savedLocation) {
        try {
          this.currentLocation = JSON.parse(savedLocation);
          // Si hay ubicación guardada, ir directo al step de scan
          this.step = "scan";
        } catch (error) {
          console.error("Error parsing saved location:", error);
          // Si hay error, mantener en scan-location
          this.step = "scan-location";
        }
      } else {
        this.step = "scan-location";
      }
    },

    loadLastCompletedOrder() {
      // Cargar el último pedido completado desde localStorage
      const savedLastCompleted = localStorage.getItem("last_completed_order");
      if (savedLastCompleted) {
        try {
          this.lastCompletedOrder = JSON.parse(savedLastCompleted);
        } catch (error) {
          console.error("Error parsing last completed order:", error);
          this.lastCompletedOrder = null;
        }
      }
    },

    saveLastCompletedOrder(completedOrder) {
      // Guardar el último pedido completado en localStorage
      try {
        localStorage.setItem("last_completed_order", JSON.stringify(completedOrder));
      } catch (error) {
        console.error("Error saving last completed order:", error);
      }
    },

    openLocationModal() {
      this.showLocationModal = true;
    },

    closeBackStepDialog() {
      this.showBackStepDialog = false;
    },

    resetAllData() {
      this.scannedLPN = "";
      this.currentLPN = "";
      this.currentContainer = null;
      this.originalAssignedLocations = []; // Limpiar assigned_locations originales
      this.selectedOrder = null;
      this.packageCount = 1;
      this.lastCompletedOrder = null;
      this.packages = [{ id: 1, items: [] }];
      this.currentPackageId = 1;
    },

    closeLocationModal() {
      if (this.currentLocation) {
        this.showLocationModal = false;
      }
    },

    handleLocationScanned(locationData) {
      const params = {
        ubicacion_code: locationData.trim(),
        tipo_ubicacion_key: "allow_packing",
      };

      ubicacionService
        .ubicationValidateScan(params)
        .then((response) => {
          if (response.status) {
            this.currentLocation = locationData;
            localStorage.setItem("current_packing_location", JSON.stringify(locationData));

            if (response.result && response.result.printer) {
              localStorage.setItem("current_packing_printer", JSON.stringify(response.result.printer));
            }

            if (this.step === "scan-location") {
              this.step = "scan";
              this.$nextTick(() => {
                if (this.$refs.scanLPNComponent) {
                  this.$refs.scanLPNComponent.focusInput();
                }
              });
            } else {
              this.closeLocationModal();
              this.resetAllData();
              this.step = "scan";
            }

            this.toast?.success(`Ubicación ${locationData} establecida correctamente`);
          } else {
            this.handleError(response.msg || "La ubicación escaneada no es válida.");
          }
        })
        .catch((e) => {
          this.handleError("Error al validar la ubicación. Inténtalo de nuevo.");
        })
        .finally(() => {
          this.loading = false;
        });
    },

    onLPNScanned(lpn) {
      if (!this.currentLocation) {
        this.handleError("Debe escanear una ubicación antes de continuar.");
        return;
      }
      packagesService
        .scan({ lpn_code: lpn, ubicacion_code: this.currentLocation })
        .then((response) => {
          if (response.status) {
            this.scannedLPN = lpn;
            this.currentLPN = lpn;

            const orders = response.result?.shipment_orders || [];
            const assignedLocations = response.result?.assigned_locations || [];

            // Guardar copia original de assigned_locations
            this.originalAssignedLocations = [...assignedLocations];

            this.currentContainer = {
              lpn,
              orders: [...orders], // Crear copia para reactivity
              assigned_locations: [...assignedLocations], // Agregar assigned_locations
              currentOrderIndex: 0,
            };
            this.step = "select-order";
          } else {
            this.handleError(response?.msg || response?.message || "Error de comunicación con el servidor");
          }
        })
        .catch((e) => {
          this.handleError("Ha ocurrido un error.");
        })
        .finally(() => {
          this.loading = false;
        });
    },

    handleSelectOrder(order) {
      this.originalSelectedOrder = this.cloneOrder(order);
      this.selectedOrder = this.cloneOrder(order);
      this.packages = [{ id: 1, items: [] }];
      this.currentPackageId = 1;

      let filteredAssignedLocations = [];

      // Filtrar assigned_locations por la orden seleccionada desde la copia original
      if (this.originalAssignedLocations && this.originalAssignedLocations.length > 0) {
        filteredAssignedLocations = this.originalAssignedLocations.filter((item) => {
          // Verificar si el assigned_location pertenece a esta orden usando el código
          return item.shipment_order_item?.shipment_order?.code === order.code;
        });

        // Actualizar currentContainer con las assigned_locations filtradas
        this.currentContainer = {
          ...this.currentContainer,
          assigned_locations: filteredAssignedLocations,
        };
      }

      // Para validación en packing usar assigned_locations (sin cancelados/con incidencias)
      const validationItems = this.buildValidationItemsFromAssignedLocations(filteredAssignedLocations);
      this.itemsFromAssignedLocations = validationItems.length > 0;
      if (this.itemsFromAssignedLocations) {
        this.selectedOrder = {
          ...this.selectedOrder,
          items: validationItems,
        };
      }

      // Verificar si el cliente tiene skip_item_validation activado
      if (order.client && order.client.skip_item_validation === true) {
        // Auto-validar todos los items
        this.selectedOrder.items = this.selectedOrder.items.map((item) => ({
          ...item,
          validated: item.amount,
        }));

        // Pre-poblar el primer paquete con todos los items del pedido
        const packageItems = this.selectedOrder.items.map((orderItem) => ({
          id: orderItem.id,
          product: {
            id: orderItem.product_presentation.product.id,
            name: orderItem.product_presentation.product.name,
            sku: orderItem.product_presentation.product.sku,
          },
          amount: orderItem.amount,
          manufacture_date: orderItem.manufacture_date || null,
          lote: orderItem.lote || null,
          expiration_date: orderItem.expiration_date || null,
          series_arr: orderItem.series_arr || null,
          quality: orderItem.quality || null,
          storage_units_amount: orderItem.storage_units_amount || null,
          descriptivo_1: orderItem.descriptivo_1 || null,
          descriptivo_2: orderItem.descriptivo_2 || null,
          descriptivo_3: orderItem.descriptivo_3 || null,
          scannedAt: new Date().toISOString(),
        }));

        this.packages = [{ id: 1, items: packageItems }];
      }

      // Ir a validación (tanto para flujo normal como auto-validación)
      this.step = "validate";

      // Hacer scroll hasta la sección de ValidateItems después de que se renderice
      this.$nextTick(() => {
        if (this.$refs.validateItemsComponent) {
          this.$refs.validateItemsComponent.$el.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
          
          // Si no es auto-validación, enfocar el input de escaneo
          if (!order.client?.skip_item_validation) {
            this.$refs.validateItemsComponent.focusInput();
          }
        }
      });
    },

    handleValidateItem(itemSku) {
      if (!this.selectedOrder) return;

      this.productoService
        .scan({ code: itemSku, lpn_code: this.currentLPN, client_id: this.selectedOrder.client?.id })
        .then((response) => {
          if (response.status && response.result) {
            const scannedProduct = response.result;

            // Cliente con skip_atributes_item_validation: el producto igual se escanea,
            // pero los atributos salen de las assigned_locations del picking (sin modal),
            // repartiendo entre las líneas pendientes del mismo producto (FEFO).
            if (this.skipAttributes) {
              this.allocateScannedUnits(scannedProduct);
              return;
            }

            const orderItem = this.selectedOrder.items.find(
              (i) =>
                i.product_presentation?.barcode === scannedProduct.base_presentation.barcode &&
                (i.validated || 0) < i.amount
            );

            if (!orderItem) {
              this.handleError("Producto no encontrado en el pedido o ya completamente validado");
              return;
            }
            
            const validated = orderItem.validated ? orderItem.validated : 0;

            if (validated >= orderItem.amount) {
              this.handleError("El producto ya fue completamente validado");
              return;
            }

            if (this.productRequiresAttributes(scannedProduct)) {
              this.openAttributesModal(scannedProduct, orderItem);
            } else {
              this.processValidatedItem(orderItem, scannedProduct, {});
            }
          } else {
            this.handleError(response?.msg || response?.message || "Error de comunicación con el servidor");
          }
        })
        .catch((error) => {
          this.handleError("Ha ocurrido un error.");
          console.error("Error en scan:", error);
        });
    },

    // Reparte las unidades escaneadas (scannedProduct.basic_units) entre las líneas
    // pendientes del mismo producto, en orden FEFO (vencimiento ascendente, sin
    // vencimiento al final). Se usa sólo cuando skipAttributes está activo: cada línea
    // ya trae resueltos sus propios atributos desde la assigned_location del picking.
    allocateScannedUnits(scannedProduct) {
      const pendingLines = this.selectedOrder.items
        .filter((i) => i.product_presentation?.barcode === scannedProduct.base_presentation.barcode && (i.validated || 0) < i.amount)
        .sort((a, b) => {
          if (!a.expiration_date && !b.expiration_date) return 0;
          if (!a.expiration_date) return 1;
          if (!b.expiration_date) return -1;
          return a.expiration_date.localeCompare(b.expiration_date);
        });

      if (pendingLines.length === 0) {
        this.handleError("Producto no encontrado en el pedido o ya completamente validado");
        return;
      }

      const totalAvailable = pendingLines.reduce((acc, line) => acc + (line.amount - (line.validated || 0)), 0);

      if (scannedProduct.basic_units > totalAvailable) {
        this.handleError(`El producto ${scannedProduct.name} no puede ser validado porque supera la cantidad del pedido (${totalAvailable})`);
        return;
      }

      let remaining = scannedProduct.basic_units;
      for (const line of pendingLines) {
        if (remaining <= 0) break;

        const lineAvailable = line.amount - (line.validated || 0);
        const qty = Math.min(remaining, lineAvailable);
        if (qty <= 0) continue;

        this.processValidatedItem(line, scannedProduct, this.resolveAttributesFromOrderItem(line), qty);
        remaining -= qty;
      }
    },

    // Traduce una línea armada por buildValidationItemsFromAssignedLocations() al shape
    // de atributos que espera processValidatedItem (el mismo que emite ModalProductAttributes).
    resolveAttributesFromOrderItem(orderItem) {
      return {
        lote: orderItem.lote || null,
        serie: orderItem.serie || null,
        expiration_date: orderItem.expiration_date || null,
        manufacture_date: orderItem.manufacture_date || null,
        quality: orderItem.quality || null,
        storage_units_amount: orderItem.storage_units_amount || null,
        descriptivo_1: orderItem.descriptivo_1 || null,
        descriptivo_2: orderItem.descriptivo_2 || null,
        descriptivo_3: orderItem.descriptivo_3 || null,
      };
    },

    handleFinishValidation() {
      this.step = "package";

      // Enfocar el botón de imprimir después de cambiar al step package
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.packageOrderComponent && this.$refs.packageOrderComponent.$refs.printButton) {
            this.$refs.packageOrderComponent.$refs.printButton.$el.focus();
          }
        }, 100);
      });
    },

  async handlePrintLabels() {
      if (this.selectedOrder && this.currentContainer && !this.loading_print_labels) {
        this.loading_print_labels = true;

        const packageBody = this.generatePackageBody();
        console.log("Generated package body for backend:", packageBody);
        
        try {
          const response = await packagesService.pack(packageBody);

          if (response.status) {
            this.toast.success("Empaquetado exitoso");

            await this.printLabelZPL(packageBody.shipment_order_code);

            // Actualizar el estado del pedido
            const completedOrder = {
              ...this.selectedOrder,
              status: "completed",
              completedAt: new Date(),
            };
            this.lastCompletedOrder = completedOrder;
            this.saveLastCompletedOrder(completedOrder); // Guardar en localStorage

            //Sacar this.selectedOrder de currentContainer.orders
            this.currentContainer = {
              ...this.currentContainer,
              orders: this.currentContainer.orders.filter((order) => order.id !== this.selectedOrder.id),
            };

            this.step = "complete";

            // Enfocar el botón correcto en OrderComplete según si hay próximo pedido
            this.$nextTick(() => {
              setTimeout(() => {
                if (this.$refs.orderCompleteComponent) {
                  // Verificar si hay próximo pedido
                  const hasNextOrder = this.getPendingOrdersCount() > 0;
                  if (hasNextOrder && this.$refs.orderCompleteComponent.$refs.nextOrderButton) {
                    this.$refs.orderCompleteComponent.$refs.nextOrderButton.$el.focus();
                  } else if (!hasNextOrder && this.$refs.orderCompleteComponent.$refs.newLPNButton) {
                    this.$refs.orderCompleteComponent.$refs.newLPNButton.$el.focus();
                  }
                }
              }, 100);
            });
          } else {
            this.handleError(response?.msg || response?.message || "Error de comunicación con el servidor");
          }
        } catch (error) {
          console.error("Error en el proceso de empaquetado:", error);
          this.handleError("Ha ocurrido un error al empaquetar.");
        } finally {
          this.loading_print_labels = false;
        }        
      }
    },

    async printLabelZPL(shipmentOrderCode) {
      try {
        this.loading_print_zpl = true;
        //viene por aca me parece
        // let code = this.selectedOrder != null ? this.selectedOrder.code : this.lastCompletedOrder.code;

        console.log("Iniciando impresión de etiquetas ZPL...", shipmentOrderCode);

        const response = await packagesService.print_label_zpl({
          shipment_order_code: shipmentOrderCode,
        });

        if (response.status) {
          const etiquetasZPL = response.result;

          const printer = JSON.parse(localStorage.getItem("current_packing_printer") ?? "null");

          if (!printer || typeof printer !== "object") {
            this.handleError("Vuelva a escanear la ubicación para configurar la impresora.");
            return;
          }

          // Imprimir cada etiqueta ZPL con delay para evitar conflictos
          for (let i = 0; i < etiquetasZPL.length; i++) {
            await this.printSingleZPL(printer, etiquetasZPL[i], i);
          }
        } else {
          this.handleError(response?.msg || response?.message || "Error de comunicación con el servidor");
        }
      } catch (error) {
        console.error("Error al imprimir etiquetas ZPL:", error);
        this.handleError("Ha ocurrido un error al imprimir las etiquetas.");
      } finally {
        this.loading_print_zpl = false;
      }
    },

    handleNextOrder() {
      if (this.currentContainer) {
        const nextPendingOrder = this.currentContainer.orders.find((order) => order.status.id === shipmentOrderService.STATUS_PREPARADO);
        if (nextPendingOrder) {
          this.selectedOrder = null;
          this.packageCount = 1;
          // Resetear bultos para siguiente pedido
          this.packages = [{ id: 1, items: [] }];
          this.currentPackageId = 1;
          this.itemsFromAssignedLocations = false;
          this.step = "select-order";
        } else {
          this.handleNewLPN();
        }
      }
    },

    handleNewLPN() {
      this.currentLPN = "";
      this.scannedLPN = "";
      this.currentContainer = null;
      this.selectedOrder = null;
      this.packageCount = 1;
      this.packages = [{ id: 1, items: [] }];
      this.currentPackageId = 1;
      this.itemsFromAssignedLocations = false;
      // Ir a scan solo si ya tenemos ubicación, sino a scan-location
      this.step = this.currentLocation ? "scan" : "scan-location";
    },

    handleBackStep() {
      this.showBackStepDialog = true;
    },

    confirmBackStep() {
      this.closeBackStepDialog();

      switch (this.step) {
        case "scan":
          this.step = "scan-location";
          this.currentContainer = null;
          this.originalAssignedLocations = [];
          this.currentLPN = "";
          this.scannedLPN = "";
          break;
        case "select-order":
          this.step = "scan";
          this.currentContainer = null;
          this.originalAssignedLocations = [];
          this.currentLPN = "";
          this.scannedLPN = "";
          break;
        case "validate":
          if (this.currentContainer && this.originalSelectedOrder) {
            this.currentContainer = {
              ...this.currentContainer,
              orders: this.currentContainer.orders.map((order) => (order.id === this.originalSelectedOrder.id ? this.cloneOrder(this.originalSelectedOrder) : order)),
            };
          }

          this.step = "select-order";
          this.selectedOrder = null;
          this.originalSelectedOrder = null;
          this.packages = [{ id: 1, items: [] }];
          this.currentPackageId = 1;
          this.itemsFromAssignedLocations = false;
          // Restaurar las assigned_locations originales en currentContainer
          if (this.currentContainer && this.originalAssignedLocations) {
            this.currentContainer = {
              ...this.currentContainer,
              assigned_locations: [...this.originalAssignedLocations],
            };
          }
          break;
        case "package":
          this.step = "validate";
          break;
        case "complete":
          this.step = "package";
          break;
        default:
          break;
      }
    },

    // Métodos de manejo de bultos
    handleAddNewPackage() {
      const newPackageId = this.packages.length + 1;
      this.packages = [...this.packages, { id: newPackageId, items: [] }];
      this.currentPackageId = newPackageId;
    },

    handleChangePackage(packageId) {
      this.currentPackageId = packageId;
    },

    handleRemoveItemFromPackage(packageId, itemToRemove) {
      const updatedPackages = this.packages.map((pkg) => {
        if (pkg.id === packageId) {
          const itemIndex = pkg.items.findIndex((item) => item.product.id === itemToRemove.product.id && item.scannedAt === itemToRemove.scannedAt && this.attributesMatch(item, itemToRemove));

          if (itemIndex !== -1) {
            const updatedItems = [...pkg.items];
            const currentItem = updatedItems[itemIndex];

            if (currentItem.amount > 1) {
              // Si hay más de 1, reducir la cantidad
              updatedItems[itemIndex] = {
                ...currentItem,
                amount: currentItem.amount - 1,
              };
            } else {
              // Si solo hay 1, eliminar el item completamente
              updatedItems.splice(itemIndex, 1);
            }

            return { ...pkg, items: updatedItems };
          }
        }
        return pkg;
      });

      this.packages = updatedPackages;

      // También actualizar el contador de validados en el pedido.
      // Se matchea por orderItem.id (= itemForPackage.id, el bucket de origen) y no por
      // product.id: un mismo producto puede tener varias líneas con distintos atributos
      // (p.ej. dos lotes), y descontar por product.id bajaría el contador de todas a la vez.
      const updatedOrder = {
        ...this.selectedOrder,
        items: this.selectedOrder.items.map((orderItem) => {
          if (orderItem.id === itemToRemove.id) {
            return {
              ...orderItem,
              validated: Math.max(0, (orderItem.validated || 0) - 1),
            };
          }
          return orderItem;
        }),
      };

      this.selectedOrder = updatedOrder;

      // Actualizar el contenedor
      if (this.currentContainer) {
        this.currentContainer = {
          ...this.currentContainer,
          orders: this.currentContainer.orders.map((order) => (order.id === this.selectedOrder.id ? updatedOrder : order)),
        };
      }
    },

    productRequiresAttributes(product) {
      return (
        product.tiene_control_por_lotes ||
        product.tiene_control_por_serie ||
        product.tiene_control_de_calidad ||
        product.tiene_control_por_descriptivo_1 ||
        product.tiene_control_por_descriptivo_2 ||
        product.tiene_control_por_descriptivo_3 ||
        product.requiere_fecha_expiracion
      );
    },

    openAttributesModal(scannedProduct, orderItem) {
      this.currentScannedProduct = scannedProduct;
      this.currentOrderItem = orderItem;
      this.showAttributesModal = true;
    },

    closeAttributesModal() {
      this.showAttributesModal = false;
      this.currentScannedProduct = null;
      this.currentOrderItem = null;

      this.$nextTick(() => {
        if (this.$refs.validateItemsComponent && this.step === "validate") {
          this.$refs.validateItemsComponent.focusInput();
        }
      });
    },

    handleAttributesSubmitted(attributes) {
      if (this.currentScannedProduct && this.currentOrderItem) {
        this.processValidatedItem(this.currentOrderItem, this.currentScannedProduct, attributes);
        this.closeAttributesModal();

        this.$nextTick(() => {
          if (this.$refs.validateItemsComponent && this.step === "validate") {
            this.$refs.validateItemsComponent.focusInput();
          }
        });
      }
    },

    processValidatedItem(orderItem, scannedProduct, attributes, qty = scannedProduct.basic_units) {
      const currentValidated = orderItem.validated || 0;

      // Validar que no supere la cantidad pendiente del item
      if (currentValidated + qty > orderItem.amount) {
        this.handleError(`El producto ${scannedProduct.name} no puede ser validado porque supera la cantidad del pedido (${orderItem.amount})`);
        return;
      }

      const updatedOrder = {
        ...this.selectedOrder,
        items: this.selectedOrder.items.map((i) => (i.id === orderItem.id ? { ...i, validated: (i.validated || 0) + qty } : i)),
      };


      this.selectedOrder = updatedOrder;
      //console.log("processValidatedItem:",scannedProduct,orderItem )
      const itemForPackage = {
        id: orderItem.id,
        product: {
          id: scannedProduct.id,
          name: scannedProduct.name,
          sku: scannedProduct.sku,
        },
        amount: qty,
        manufacture_date: attributes.manufacture_date || null,
        lote: attributes.lote || null,
        expiration_date: attributes.expiration_date || null,
        series_arr: attributes.serie ? [{ serie: attributes.serie }] : null,
        quality: attributes.quality || null,
        storage_units_amount: attributes.storage_units_amount || null,
        descriptivo_1: attributes.descriptivo_1 || null,
        descriptivo_2: attributes.descriptivo_2 || null,
        descriptivo_3: attributes.descriptivo_3 || null,
        scannedAt: new Date().toISOString(),
      };

      const updatedPackages = this.packages.map((pkg) => {
        if (pkg.id === this.currentPackageId) {
          // Buscar si ya existe un item con los mismos atributos
          const existingItemIndex = pkg.items.findIndex((pi) => pi.product.sku === scannedProduct.sku && this.attributesMatch(pi, itemForPackage));

          if (existingItemIndex !== -1) {
            // Si existe con los mismos atributos, incrementar cantidad
            const updatedItems = [...pkg.items];
            updatedItems[existingItemIndex] = {
              ...updatedItems[existingItemIndex],
              amount: updatedItems[existingItemIndex].amount + qty,
            };
            return { ...pkg, items: updatedItems };
          } else {
            // Si no existe o tiene atributos diferentes, agregar como nuevo item
            return {
              ...pkg,
              items: [...pkg.items, itemForPackage],
            };
          }
        }
        return pkg;
      });

      this.packages = updatedPackages;

      // Actualizar el contenedor
      if (this.currentContainer) {
        this.currentContainer = {
          ...this.currentContainer,
          orders: this.currentContainer.orders.map((order) => (order.id === this.selectedOrder.id ? updatedOrder : order)),
        };
      }
    },

    attributesMatch(item1, item2) {
      if (!item1 && !item2) return true;
      if (!item1 || !item2) return false;

      // Comparar series arrays
      const series1 = item1.series_arr ? item1.series_arr[0]?.serie : null;
      const series2 = item2.series_arr ? item2.series_arr[0]?.serie : null;
      const quality1Id = item1.quality?.id ?? null;
      const quality2Id = item2.quality?.id ?? null;

      return (
        item1.lote === item2.lote &&
        series1 === series2 &&
        item1.descriptivo_1 === item2.descriptivo_1 &&
        item1.descriptivo_2 === item2.descriptivo_2 &&
        item1.descriptivo_3 === item2.descriptivo_3 &&
        item1.expiration_date === item2.expiration_date &&
        item1.manufacture_date === item2.manufacture_date &&
        quality1Id === quality2Id
      );
    },

    buildValidationItemsFromAssignedLocations(assignedLocations = []) {
      if (!Array.isArray(assignedLocations) || assignedLocations.length === 0) {
        return [];
      }

      return assignedLocations
        .map((al) => {
          const pa = al?.presentation_atributes || {};
          const soi = al?.shipment_order_item || {};
          const productPresentation = pa?.product_presentation || soi?.product_presentation || null;
          const amount = Number(al?.available_stock ?? al?.amount ?? 0);

          if (!productPresentation || !Number.isFinite(amount) || amount <= 0) {
            return null;
          }

          return {
            id: `al-${al?.id ?? `${soi?.id ?? 'x'}-${pa?.id ?? 'x'}`}`,
            product_presentation: productPresentation,
            amount: amount,
            validated: 0,
            manufacture_date: pa?.manufacture_date || soi?.manufacture_date || null,
            lote: pa?.lote || soi?.lote || null,
            expiration_date: pa?.expiration_date || soi?.expiration_date || null,
            serie: pa?.serie || soi?.serie || null,
            quality: pa?.quality || soi?.quality || null,
            storage_units_amount: pa?.storage_units_amount || soi?.storage_units_amount || null,
            descriptivo_1: pa?.descriptivo_1 || soi?.descriptivo_1 || null,
            descriptivo_2: pa?.descriptivo_2 || soi?.descriptivo_2 || null,
            descriptivo_3: pa?.descriptivo_3 || soi?.descriptivo_3 || null,
          };
        })
        .filter(Boolean);
    },

    getNextPendingOrder() {
      return this.currentContainer?.orders.find((order) => order.status.id === shipmentOrderService.STATUS_PREPARADO) || null;
    },

    getPendingOrdersCount() {
      return this.currentContainer?.orders.filter((order) => order.status.id === shipmentOrderService.STATUS_PREPARADO).length || 0;
    },

    generatePackageBody() {
      // Método para generar el body del backend
      const bultos_arr = this.packages.map((pkg) => {
        return pkg.items.map((item) => ({
          product: item.product,
          amount: item.amount,
          manufacture_date: item.manufacture_date,
          lote: item.lote,
          expiration_date: item.expiration_date,
          series_arr: item.series_arr,
          quality: item.quality,
          descriptivo_1: item.descriptivo_1,
          descriptivo_2: item.descriptivo_2,
          descriptivo_3: item.descriptivo_3,
          storage_units_amount: item.storage_units_amount,
        }));
      });

      return {
        lpn_code: this.currentLPN,
        shipment_order_code: this.selectedOrder?.code,
        bultos_arr: bultos_arr,
      };
    },

    handleError(message) {
      this.errorMessage = message || "Ha ocurrido un error inesperado";
      this.showError = true;
    },

    closeError() {
      this.showError = false;
      this.errorMessage = "";

      this.$nextTick(() => {
        if (this.$refs.scanLocationComponent && this.step === "scan-location") {
          this.$refs.scanLocationComponent.focusInput();
        } else if (this.$refs.scanLPNComponent && this.step === "scan") {
          this.$refs.scanLPNComponent.focusInput();
        } else if (this.$refs.validateItemsComponent && this.step === "validate") {
          this.$refs.validateItemsComponent.focusInput();
        }

        // Si el modal de ubicación está abierto, enfocar su input
        if (this.showLocationModal && this.$refs.modalScanLocation) {
          this.$refs.modalScanLocation.focusInput();
        }
      });
    },
  },
};
</script>

<style scoped>
.space-y-6 > * + * {
  margin-top: 24px;
}

.max-w-6xl {
  max-width: 72rem;
}

.mx-auto {
  margin-left: auto;
  margin-right: auto;
}
</style>
